import { useState, useRef, useEffect } from "react";

/* ─── API ─────────────────────────────────────────────────── */
const API_URL = "https://apiloan.a2synergy.pl/api/skd/calculate";
const LEADS_URL = "https://apiloan.a2synergy.pl/api/leads";
const API_KEY = "lck_9645ba89e0cdf1cea84011cb59e0b510913561910c84c264";
const UKK_MAX = 255550;
const UKK_MIN = 30000;

/* ─── Formatters ─────────────────────────────────────────── */
const num = (n, d = 2) =>
  n != null
    ? new Intl.NumberFormat("pl-PL", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(n)
    : "—";
const pln = (n) => (n != null ? `${num(n)} zł` : "—");
const pct = (n) => (n != null ? `${num(n * 100)} %` : "—");
const fmtD = (d) => new Date(d).toLocaleDateString("pl-PL");

/* ─── Banks ──────────────────────────────────────────────── */
const BANKS = [
  "Alior Bank",
  "Bank BPH",
  "Bank Millennium",
  "Bank Pekao",
  "BNP Paribas",
  "BOŚ Bank",
  "Credit Agricole",
  "Deutsche Bank",
  "Getin Noble Bank / VeloBank",
  "ING Bank Śląski",
  "mBank",
  "PKO Bank Polski",
  "Raiffeisen Bank",
  "Santander Bank Polska",
  "Toyota Bank",
  "Inny",
];

/* ─── Phone validation ───────────────────────────────────── */
const normalizePhone = (raw) => {
  let s = raw.replace(/[\s\-.()/]/g, "");
  if (s.startsWith("+")) s = s.slice(1);
  if (s.startsWith("48") && s.length === 11) s = s.slice(2);
  return s;
};
const isValidPhone = (raw) => /^\d{9}$/.test(normalizePhone(raw));

/* ─── Full name split ────────────────────────────────────── */
const splitFullName = (full) => {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || "";
  const lastName = parts.join(" ");
  return { firstName, lastName };
};

const CAPI_URL = "/api/events/capi";
const sendCapi = (eventName, payload = {}) => {
  const event_id = `${eventName}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
  fetch(CAPI_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_name: eventName,
      event_id,
      source_url: typeof window !== "undefined" ? window.location.href : "",
      ...payload,
    }),
  }).catch((e) => console.warn("[CAPI] send error:", e));
};

/* ─── Kancelarie data ────────────────────────────────────── */
const FIRMS = [
  {
    logo: "https://kppp.pl/wp-content/uploads/2023/03/Logo-2_SVG.svg",
    alt: "Kancelaria Prawna Mateusz Płudowski",
    since: "Sprawy bankowe od 2019 roku",
    cases: "~4 000",
    wins: "~1 800",
    desc: "Specjalizuje się w kompleksowej obsłudze kredytobiorców z umowami walutowymi, złotowymi oraz Sankcją Kredytu Darmowego. Prowadzi sprawy na terenie całej Polski.",
    pills: ["SKD", "Kredyty Walutowe", "Kredyty złotowe", "Słuebność przesyłu"],
  },
  {
    logo: "https://www.kancelaria-olc.pl/img/logo.png",
    alt: "Kancelaria OLC",
    since: "Sprawy bankowe od 2016 roku",
    cases: "~1 400",
    wins: "~1 000",
    desc: "Łączy szeroką obsługę prawną podmiotów gospodarczych z wyspecjalizowanym działem sporów bankowych — kredyty CHF, EUR, USD, SKD oraz odzyskiwanie nienależnych opłat z polisolokat i UNWW. Działa na terenie całej Polski.",
    pills: ["SKD", "Kredyty Walutowe", "Polisolokaty", "Obsługa firm"],
  },
];

/* ─── Icons ──────────────────────────────────────────────── */
const IC = {
  money: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect
        x="1"
        y="3"
        width="14"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M1 6h2M13 6h2M1 10h2M13 10h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  hash: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 2L4.5 14M11.5 2L10 14M2.5 6h11M2 10h11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  percent: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="4.5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.5" />
      <circle
        cx="11.5"
        cy="11.5"
        r="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M13 3L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  calendar: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect
        x="1.5"
        y="2.5"
        width="13"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 1v3M11 1v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="5" cy="10" r="1" fill="currentColor" />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
      <circle cx="11" cy="10" r="1" fill="currentColor" />
    </svg>
  ),
  bank: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1.5L14.5 5H1.5L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <rect x="2.5" y="5" width="2" height="7" fill="currentColor" rx="0.5" />
      <rect x="7" y="5" width="2" height="7" fill="currentColor" rx="0.5" />
      <rect x="11.5" y="5" width="2" height="7" fill="currentColor" rx="0.5" />
      <path
        d="M1 12.5h14M1 14.5h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  user: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  mail: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect
        x="1.5"
        y="3.5"
        width="13"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M1.5 5l6.5 4.5L14.5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  phone: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 2h3l1.5 3.5-2 1.5c1 2 2.5 3.5 4.5 4.5l1.5-2L15 11v3c0 .5-.5 1-1 1C5 14.5 1.5 9.5 2 4c0-.5.5-1 1-1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
  file: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M9 1.5H4a1.5 1.5 0 00-1.5 1.5v10A1.5 1.5 0 004 14.5h8a1.5 1.5 0 001.5-1.5V6L9 1.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M9 1.5V6H13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
  pdf: (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 1.5h7l3 3V14A.5.5 0 0112.5 14.5h-9A.5.5 0 013 14V2a.5.5 0 010-1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M10 1.5V4.5H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 9.5h5M5.5 11.5h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  chart: (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
      <rect
        x="2"
        y="12"
        width="4"
        height="9"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <rect
        x="10"
        y="7"
        width="4"
        height="14"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <rect
        x="18"
        y="3"
        width="4"
        height="18"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  ),
  shield: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1.5L2 4v4c0 3.5 2.5 6 6 7 3.5-1 6-3.5 6-7V4L8 1.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 8l1.5 1.5 3-3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  star: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1l1.854 3.757 4.146.603-3 2.923.708 4.126L8 10.355l-3.708 1.954.708-4.126L2 5.36l4.146-.603z" />
    </svg>
  ),
  arrowRight: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

  .skdc-wrap { box-sizing: border-box; margin: 0 auto; }
  .skdc-wrap * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  .skdc-grid { display: grid; grid-template-columns: 50% 50%; gap: 20px; align-items: start; }
  .skdc-right-col { position: sticky; top: 20px; }

  .skdc-card { background: #fff; border-radius: 18px; padding: 26px; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 6px 28px rgba(10,46,44,.08); }

  .skdc-fg { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
  .skdc-fg .skdc-s2 { grid-column: span 2; }

  .skdc-lbl { display: block; font-size: 11px; font-weight: 600; color: #7a9e98; letter-spacing: .07em; text-transform: uppercase; margin-bottom: 6px; }
  .skdc-iw { position: relative; }
  .skdc-ii { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #a8c8c2; pointer-events: none; display: flex; align-items: center; }
  .skdc-inp { width: 100%; padding: 11px 13px 11px 38px; border: 1.5px solid #dde8e5; border-radius: 10px; font-size: 14px; color: #0a2e2c; background: #fff; outline: none; transition: border-color .14s, box-shadow .14s; -webkit-appearance: none; min-height: 46px; box-sizing: border-box; display: block; }
  .skdc-inp.skdc-bare { padding-left: 13px; }
  .skdc-inp:hover:not(:focus) { border-color: #b4ceca; }
  .skdc-inp:focus { border-color: #0f766e; box-shadow: 0 0 0 3px rgba(15,118,110,.11); }
  .skdc-inp.err { border-color: #dc2626; box-shadow: 0 0 0 3px rgba(220,38,38,.09); }

  .skdc-hint  { font-size: 11px; color: #9ab0aa; margin-top: 4px; }
  .skdc-hwarn { font-size: 11px; color: #b45309; margin-top: 4px; }

  .skdc-section-head { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; }
  .skdc-section-icon { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .skdc-section-icon.teal { background: #e8f5f2; color: #0f766e; }
  .skdc-section-title { font-size: 13px; font-weight: 700; color: #0a2e2c; line-height: 1.2; }
  .skdc-section-sub   { font-size: 11px; color: #9ab0aa; margin-top: 1px; }

  .skdc-btn { width: 100%; padding: 13px; border: none; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; letter-spacing: .01em; transition: background .14s, box-shadow .14s, transform .07s; display: block; text-align: center; }
  .skdc-btn:active { transform: scale(.99); }
  .skdc-btn-p { background:#0f766e; color:#fff; box-shadow:0 2px 10px rgba(15,118,110,.28); }
  .skdc-btn-p:hover { background:#0d6460; }
  .skdc-btn-p:disabled { background:#a8c8c2; box-shadow:none; cursor:not-allowed; }

  .skdc-ebox { background:#fef2f2; border:1.5px solid #fca5a5; border-radius:9px; padding:9px 13px; color:#dc2626; font-size:13px; margin-top:10px; }

  .mg { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  .mg-wide { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 768px) { .mg-wide { grid-template-columns: repeat(3, 1fr); } }
  .skdc-mc { background:#fff; border:1.5px solid #e4eeec; border-radius:13px; padding:13px 12px 11px; transition:box-shadow .14s, border-color .14s; }
  .skdc-mc:hover { box-shadow:0 4px 18px rgba(10,46,44,.1); border-color:#bed8d2; }
  .skdc-mc-l { font-size:10px; font-weight:600; color:#a8c8c2; text-transform:uppercase; letter-spacing:.07em; margin-bottom:4px; line-height:1.3; }
  .skdc-mc-v { font-size:16px; font-weight:700; color:#0f766e; line-height:1.2; }
  a.skdc-mc { text-decoration:none; display:flex; flex-direction:column; justify-content:space-between; }

  .skdc-dl-ok   { background:#f0fdf9; border:1.5px solid #c8e4df; border-radius:9px; padding:9px 13px; font-size:12px; color:#0d6460; }
  .skdc-dl-warn { background:#fffbeb; border:1.5px solid #fcd34d; border-radius:9px; padding:9px 13px; font-size:12px; color:#92400e; }
  .skdc-dl-exp  { background:#fef2f2; border:1.5px solid #fca5a5; border-radius:9px; padding:9px 13px; font-size:12px; color:#991b1b; font-weight:600; }

  /* ── Kancelarie slider ── */
  .skdc-trust-header { background: #fff; border: 1.5px solid #e4eeec; border-radius: 14px; padding: 14px 18px; margin-bottom: 10px; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 6px 28px rgba(10,46,44,.08); }
  .skdc-trust-top { display: flex; align-items: center; gap: 10px; margin-bottom: 13px; }
  .skdc-trust-icon { width: 32px; height: 32px; background: #e8f5f2; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #0f766e; }
  .skdc-trust-title { font-size: 13px; font-weight: 700; color: #0a2e2c; }
  .skdc-trust-sub   { font-size: 11px; color: #9ab0aa; margin-top: 1px; }
  .skdc-total-bar   { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .skdc-total-cell  { background: #f0fdf9; border: 1px solid #c8e4df; border-radius: 10px; padding: 11px 14px; text-align: center; }
  .skdc-total-num { font-size: 22px; font-weight: 700; color: #0f766e; line-height: 1; }
  .skdc-total-lbl { font-size: 10px; font-weight: 600; color: #7ab4ac; text-transform: uppercase; letter-spacing: .06em; margin-top: 3px; }

  .skdc-firm-card { background: #fff; border: 1.5px solid #e4eeec; border-radius: 14px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 6px 28px rgba(10,46,44,.08); transition: opacity .28s ease; }
  .skdc-firm-card.skdc-fading { opacity: 0; }
  .skdc-firm-head { padding: 14px 16px 12px; }
  .skdc-firm-logo-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .skdc-firm-logo-box { height: 36px; display: flex; align-items: center; }
  .skdc-firm-logo-box img { height: 36px; width: auto; max-width: 140px; object-fit: contain; display: block; }
  .skdc-firm-since { display: flex; align-items: center; gap: 5px; background: #e8f5f2; border: 1px solid #c8e4df; border-radius: 7px; padding: 3px 9px; font-size: 10.5px; font-weight: 600; color: #0f766e; white-space: nowrap; }
  .skdc-firm-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
  .skdc-firm-stat  { background: #f8fdfb; border: 1px solid #e8f0ed; border-radius: 9px; padding: 9px 12px; }
  .skdc-firm-stat-n { font-size: 20px; font-weight: 700; color: #0f766e; line-height: 1; }
  .skdc-firm-stat-l { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #a8c0bc; margin-top: 3px; }
  .skdc-firm-desc { padding: 10px 16px 14px; font-size: 12.5px; color: #4a6e68; line-height: 1.65; border-top: 1px solid #f0f4f2; }
  .skdc-firm-pills { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
  .skdc-firm-pill { background: #f0fdf9; border: 1px solid #c8e4df; border-radius: 6px; padding: 3px 9px; font-size: 11px; font-weight: 500; color: #0d6460; }

  .skdc-slider-nav { display: flex; align-items: center; justify-content: space-between; padding: 10px 2px 0; }
  .skdc-nav-dots { display: flex; gap: 6px; align-items: center; }
  .skdc-dot { width: 6px; height: 6px; border-radius: 50%; background: #dde8e5; border: none; padding: 0; cursor: pointer; transition: background .25s, transform .25s; }
  .skdc-dot.active { background: #0f766e; transform: scale(1.35); }
  .skdc-nav-arrows { display: flex; gap: 6px; }
  .skdc-arrow-btn { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #dde8e5; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #5a8a84; transition: border-color .14s, color .14s, background .14s; }
  .skdc-arrow-btn:hover { border-color: #0f766e; color: #0f766e; background: #f0fdf9; }

  .skdc-progress-bar { height: 2px; background: #e4eeec; border-radius: 2px; margin-top: 10px; overflow: hidden; }
  .skdc-progress-fill { height: 100%; background: #0f766e; border-radius: 2px; }

  .skdc-trust-note { background: #f4faf8; border: 1px solid #dde8e5; border-radius: 10px; padding: 10px 14px; font-size: 11.5px; color: #6b8f89; line-height: 1.6; display: flex; gap: 8px; align-items: flex-start; margin-top: 10px; }
  .skdc-trust-note-icon { flex-shrink: 0; margin-top: 1px; color: #a8c8c2; }

  .skdc-fdrop { border:1.5px dashed #b4ceca; border-radius:10px; padding:15px 14px; text-align:center; cursor:pointer; transition:background .14s, border-color .14s; background:#f8fdfb; }
  .skdc-fdrop:hover, .skdc-fdrop.skdc-drag { background:#f0fdf9; border-color:#0f766e; }
  .skdc-fitem { display:flex; align-items:center; gap:8px; padding:7px 10px; background:#f8fdfb; border:1px solid #e4eeec; border-radius:8px; margin-top:5px; font-size:12px; color:#334155; }
  .skdc-frm { margin-left:auto; background:none; border:none; color:#dc2626; font-size:18px; cursor:pointer; line-height:1; }

  @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
  .skdc-au { animation: fadeUp .28s ease both; }

  .skdc-summary-card { background: #fff; border-radius: 18px; padding: 18px 22px; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 6px 28px rgba(10,46,44,.08); animation: fadeUp .22s ease both; }
  .skdc-summary-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .skdc-summary-bank { font-size: 15px; font-weight: 700; color: #0a2e2c; }
  .skdc-summary-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 2px; }
  .skdc-chip { background: #f0fdf9; border: 1px solid #c8e4df; border-radius: 7px; padding: 4px 10px; font-size: 11.5px; font-weight: 600; color: #0d6460; white-space: nowrap; }
  .skdc-btn-reset { display: flex; align-items: center; gap: 7px; background: none; border: 1.5px solid #dde8e5; border-radius: 9px; padding: 7px 13px; font-size: 12px; font-weight: 600; color: #7a9e98; cursor: pointer; transition: border-color .14s, color .14s; white-space: nowrap; flex-shrink: 0; }
  .skdc-btn-reset:hover { border-color: #0f766e; color: #0f766e; }

  @keyframes slideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:none; } }
  .skdc-slide-down { animation: slideDown .36s ease both; overflow:hidden; }

  .skdc-lead-card { background: #fff; border-radius: 18px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 8px 36px rgba(10,46,44,.13); border: 1.5px solid #dde8e5; margin-top: 12px; }
  .skdc-lead-hero { background: linear-gradient(135deg, #042e2a 0%, #0f766e 60%, #14b8a6 100%); padding: 22px 22px 20px; position: relative; overflow: hidden; }
  .skdc-lead-hero::before { content: ''; position: absolute; top: -30px; right: -30px; width: 140px; height: 140px; background: rgba(255,255,255,.04); border-radius: 50%; }
  .skdc-lead-hero::after { content: ''; position: absolute; bottom: -50px; left: 20px; width: 180px; height: 180px; background: rgba(255,255,255,.03); border-radius: 50%; }
  .skdc-lead-amount { font-size: 34px; font-weight: 700; color: #f5d48a; line-height: 1; margin: 0 0 4px; letter-spacing: -.5px; }
  .skdc-lead-amount-label { font-size: 11px; color: rgba(255,255,255,.55); text-transform: uppercase; letter-spacing: .1em; font-weight: 600; margin-bottom: 12px; }
  .skdc-lead-tagline { font-size: 15px; color: #fff; font-weight: 600; line-height: 1.45; margin: 0 0 14px; }
  .skdc-lead-bullets { display: flex; flex-direction: column; gap: 7px; }
  .skdc-lead-bullet { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: rgba(255,255,255,.82); font-weight: 500; }
  .skdc-lead-bullet-icon { width: 22px; height: 22px; border-radius: 6px; background: rgba(255,255,255,.13); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #a3e9d9; }
  .skdc-lead-body { padding: 20px 22px 22px; }
  .skdc-lead-form-title { font-size: 14px; font-weight: 700; color: #0a2e2c; margin: 0 0 4px; }
  .skdc-lead-form-sub { font-size: 12px; color: #9ab0aa; margin: 0 0 16px; line-height: 1.5; }
  .skdc-social-proof { display: flex; align-items: center; gap: 8px; background: #f8fdfb; border: 1px solid #e4eeec; border-radius: 9px; padding: 8px 12px; margin-bottom: 16px; }
  .skdc-stars { display: flex; gap: 2px; color: #f59e0b; }
  .skdc-sp-text { font-size: 11.5px; color: #4a6e68; font-weight: 500; }
  .skdc-optout { display: flex; align-items: flex-start; gap: 8px; background: #f8fdfb; border: 1px solid #dde8e5; border-radius: 9px; padding: 9px 12px; margin-top: 13px; font-size: 11px; color: #6b8f89; line-height: 1.6; }
  .skdc-optout svg { margin-top: 1px; flex-shrink: 0; color: #a8c8c2; }
  .skdc-btn-lead { width: 100%; padding: 14px; border: none; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; letter-spacing: .01em; transition: all .15s; display: flex; align-items: center; justify-content: center; gap: 9px; background: linear-gradient(135deg, #0f766e, #0d9488); color: #fff; box-shadow: 0 3px 14px rgba(15,118,110,.38); }
  .skdc-btn-lead:hover { background: linear-gradient(135deg, #0d6460, #0f766e); box-shadow: 0 5px 20px rgba(15,118,110,.45); transform: translateY(-1px); }
  .skdc-btn-lead:active { transform: translateY(0); }
  .skdc-btn-lead:disabled { background: #a8c8c2; box-shadow:none; cursor:not-allowed; transform:none; }

  @media (max-width: 768px) {
    .skdc-wrap { padding: 18px 14px 56px; }
    .skdc-grid { grid-template-columns: 1fr; gap: 16px; }
    .skdc-right-col { position: static; }
    .skdc-card { padding: 18px; border-radius: 14px; }
    .mg { grid-template-columns: 1fr 1fr; }
    .skdc-inp { font-size: 16px; }
    .skdc-lead-card { border-radius: 14px; }
    .skdc-lead-hero { padding: 18px; }
    .skdc-lead-body { padding: 16px 18px 18px; }
    .skdc-fg { grid-template-columns: 1fr; }
    .skdc-fg .skdc-s2 { grid-column: span 1; }
  }
  input[type="date"].skdc-inp { height: 46px; line-height: normal; padding-top: 0; padding-bottom: 0; display: flex; align-items: center; }

  /* ── Przełącznik trybów ── */
  .skdc-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
  .skdc-mode { text-align: left; background: #fff; border: 1.5px solid #dde8e5; border-radius: 13px; padding: 13px 16px; cursor: pointer; transition: border-color .14s, box-shadow .14s, background .14s; display: flex; flex-direction: column; gap: 2px; }
  .skdc-mode:hover:not(.active) { border-color: #b4ceca; }
  .skdc-mode.active { border-color: #0f766e; background: #f0fdf9; box-shadow: 0 0 0 3px rgba(15,118,110,.1); }
  .skdc-mode-t { font-size: 13.5px; font-weight: 700; color: #0a2e2c; line-height: 1.25; }
  .skdc-mode-s { font-size: 11.5px; color: #7a9e98; line-height: 1.3; }
  @media (max-width: 560px) { .skdc-modes { grid-template-columns: 1fr; } }

  /* ── Textarea ── */
  .skdc-ta { min-height: 120px; padding: 11px 13px; resize: vertical; line-height: 1.55; font-family: inherit; }

  /* ── Ekran potwierdzenia (prosty formularz) ── */
  .skdc-simple-done { background: #fff; border: 1.5px solid #c8e4df; border-radius: 18px; padding: 38px 30px; text-align: center; max-width: 560px; margin: 0 auto; box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 6px 28px rgba(10,46,44,.08); }
  .skdc-simple-done-ic { width: 56px; height: 56px; border-radius: 50%; background: #0f766e; color: #fff; font-size: 30px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
  .skdc-simple-done h3 { font-size: 20px; font-weight: 700; color: #0a2e2c; margin: 0 0 12px; }
  .skdc-simple-done p { font-size: 14px; color: #4a6a64; line-height: 1.65; margin: 0 0 14px; }
  .skdc-simple-steps { text-align: left; max-width: 380px; margin: 14px auto 22px; display: flex; flex-direction: column; gap: 10px; }
  .skdc-simple-step { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: #335; line-height: 1.5; }
  .skdc-simple-step b { color: #0a2e2c; }
  .skdc-simple-step-n { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; background: #e8f5f2; color: #0f766e; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }

  /* ── CTA: zachęta do samodzielnej analizy ── */
  .skdc-self-cta { width: 100%; display: flex; align-items: center; gap: 13px; text-align: left; background: linear-gradient(135deg, #042e2a 0%, #0f766e 62%, #14b8a6 100%); border: none; border-radius: 13px; padding: 14px 16px; margin-bottom: 18px; cursor: pointer; box-shadow: 0 4px 16px rgba(15,118,110,.28); transition: transform .08s, box-shadow .14s; }
  .skdc-self-cta:hover { box-shadow: 0 6px 22px rgba(15,118,110,.4); transform: translateY(-1px); }
  .skdc-self-cta:active { transform: translateY(0); }
  .skdc-self-cta-spark { font-size: 20px; line-height: 1; flex-shrink: 0; }
  .skdc-self-cta-txt { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
  .skdc-self-cta-t { font-size: 14px; font-weight: 700; color: #fff; line-height: 1.25; }
  .skdc-self-cta-s { font-size: 11.5px; color: rgba(255,255,255,.78); line-height: 1.3; }
  .skdc-self-cta-btn { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; background: #f5d48a; color: #042e2a; font-size: 12.5px; font-weight: 700; padding: 9px 13px; border-radius: 9px; white-space: nowrap; }
  .skdc-self-cta:hover .skdc-self-cta-btn { background: #ffe3a3; }
  @media (max-width: 560px) {
    .skdc-self-cta { flex-wrap: wrap; }
    .skdc-self-cta-btn { width: 100%; justify-content: center; }
  }
`;

/* ─── Field ──────────────────────────────────────────────── */
const Field = ({ icon, label, children, hint, warnHint, span }) => (
  <div className={span ? "skdc-s2" : ""}>
    <label className="skdc-lbl">{label}</label>
    <div className="skdc-iw">
      {icon && <span className="skdc-ii">{icon}</span>}
      {children}
    </div>
    {hint && <p className="skdc-hint">{hint}</p>}
    {warnHint && <p className="skdc-hwarn">⚠ {warnHint}</p>}
  </div>
);

/* ─── Deadline banner ────────────────────────────────────── */
const DeadlineBanner = ({ skdStatus }) => {
  if (!skdStatus) return null;
  const { skdDeadline, skdDeadlineExpired, daysUntilDeadline } = skdStatus;
  if (skdDeadlineExpired)
    return (
      <div className="skdc-dl-exp" style={{ marginTop: 8 }}>
        ✗ Termin SKD wygasł {fmtD(skdDeadline)}
      </div>
    );
  if (daysUntilDeadline <= 90)
    return (
      <div className="skdc-dl-warn" style={{ marginTop: 8 }}>
        ⚠ Termin SKD upływa <strong>{fmtD(skdDeadline)}</strong> — pozostało{" "}
        <strong>{daysUntilDeadline} dni</strong>. Pilnie złóż oświadczenie.
      </div>
    );
  return (
    <div className="skdc-dl-ok" style={{ marginTop: 8 }}>
      ✓ Termin SKD upływa <strong>{fmtD(skdDeadline)}</strong> — pozostało{" "}
      <strong>{daysUntilDeadline} dni</strong>
    </div>
  );
};

/* ─── Kancelarie Placeholder (slider) ───────────────────── */
const KancelariePlaceholder = () => {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);
  const fillRef = useRef(null);

  const goTo = (idx) => {
    if (idx === current) return;
    clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setCurrent(idx);
      setFading(false);
      setTick((t) => t + 1);
    }, 280);
  };

  const next = () => goTo((current + 1) % FIRMS.length);
  const prev = () => goTo((current - 1 + FIRMS.length) % FIRMS.length);

  useEffect(() => {
    // Animate progress bar from 0 → 100% over 10s
    const el = fillRef.current;
    if (el) {
      el.style.transition = "none";
      el.style.width = "0%";
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          el.style.transition = "width 10s linear";
          el.style.width = "100%";
        })
      );
    }
    timerRef.current = setTimeout(next, 10000);
    return () => clearTimeout(timerRef.current);
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const firm = FIRMS[current];

  return (
    <div>
      <div className="skdc-trust-header">
        <div className="skdc-trust-top">
          <div className="skdc-trust-icon">{IC.shield}</div>
          <div>
            <div className="skdc-trust-title">
              Zaufane kancelarie partnerskie
            </div>
            <div className="skdc-trust-sub">
              Sprawy bankowe w całej Polsce — bezpłatna analiza umowy
            </div>
          </div>
        </div>
        <div className="skdc-total-bar">
          <div className="skdc-total-cell">
            <div className="skdc-total-num">~5 400</div>
            <div className="skdc-total-lbl">Łącznie spraw</div>
          </div>
          <div className="skdc-total-cell">
            <div className="skdc-total-num">~2 800</div>
            <div className="skdc-total-lbl">Wygranych sporów</div>
          </div>
        </div>
      </div>

      <div className={`skdc-firm-card${fading ? " skdc-fading" : ""}`}>
        <div className="skdc-firm-head">
          <div className="skdc-firm-logo-row">
            <div className="skdc-firm-logo-box">
              <img src={firm.logo} alt={firm.alt} />
            </div>
            <div className="skdc-firm-since">
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.5L2 4v4c0 3.5 2.5 6 6 7 3.5-1 6-3.5 6-7V4L8 1.5z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
              {firm.since}
            </div>
          </div>
          <div className="skdc-firm-stats">
            <div className="skdc-firm-stat">
              <div className="skdc-firm-stat-n">{firm.cases}</div>
              <div className="skdc-firm-stat-l">Prowadzonych spraw</div>
            </div>
            <div className="skdc-firm-stat">
              <div className="skdc-firm-stat-n">{firm.wins}</div>
              <div className="skdc-firm-stat-l">Wygranych w sądzie</div>
            </div>
          </div>
        </div>
        <div className="skdc-firm-desc">
          {firm.desc}
          <div className="skdc-firm-pills">
            {firm.pills.map((p) => (
              <span key={p} className="skdc-firm-pill">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="skdc-slider-nav">
        <div className="skdc-nav-dots">
          {FIRMS.map((_, i) => (
            <button
              key={i}
              className={`skdc-dot${i === current ? " active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Kancelaria ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="skdc-progress-bar">
        <div ref={fillRef} className="skdc-progress-fill" />
      </div>

      <div className="skdc-trust-note">
        <span className="skdc-trust-note-icon">{IC.shield}</span>
        <span>
          Po zgłoszeniu skontaktuje się z Tobą <strong>jedna kancelaria</strong>{" "}
          — dobrana do Twojej sprawy. Analiza umowy jest{" "}
          <strong>bezpłatna i niezobowiązująca</strong>. Prezentowane ilości
          prowadzonych oraz wygranych spraw stanowią sumę wszystkich spraw
          przeciwko bankom prowadzone przez kancelarie.
        </span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
export default function SKDCalculator() {
  const _p = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const BANK_LIST = [
    "Alior Bank",
    "Bank BPH",
    "Bank Millennium",
    "Bank Pekao",
    "BNP Paribas",
    "BOŚ Bank",
    "Credit Agricole",
    "Deutsche Bank",
    "Getin Noble Bank / VeloBank",
    "ING Bank Śląski",
    "mBank",
    "PKO Bank Polski",
    "Raiffeisen Bank",
    "Santander Bank Polska",
    "Toyota Bank",
  ];
  const _bankParam = _p.get("bank") || "";
  const _isCustom = _bankParam && !BANK_LIST.includes(_bankParam);

  const [form, setForm] = useState({
    loanAmount: _p.get("amount") || "",
    loanTermMonths: _p.get("months") || "",
    margin: _p.get("margin") || "",
    wiborTenor: "3M",
    startDate: _p.get("date") || "2020-01-01",
    commissionPLN: _p.get("commission") || "0",
  });
  const [bankSelect, setBankSelect] = useState(_isCustom ? "Inny" : _bankParam);
  const [bankCustom, setBankCustom] = useState(_isCustom ? _bankParam : "");
  const bankName = bankSelect === "Inny" ? bankCustom.trim() : bankSelect;

  const [isRepaid, setIsRepaid] = useState(false);
  const [repaidDate, setRepaidDate] = useState("");

  const [lead, setLead] = useState({
    fullName: "",
    email: "",
    phone: "",
    rodoConsent: false,
  });
  const [phoneError, setPhoneError] = useState(false);

  const [result, setResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState(null);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadDuplicate, setLeadDuplicate] = useState(null);

  /* ─── Tryb: "simple" (zostaw dane) | "calculator" (policz sam) ─── */
  const [mode, setMode] = useState("calculator");

  /* ─── Opcja 1: prosty formularz ─── */
  const [simpleLead, setSimpleLead] = useState({
    fullName: "",
    email: "",
    phone: "",
    creditDetails: "",
    rodoConsent: false,
  });
  const [simplePhoneError, setSimplePhoneError] = useState(false);
  const [simpleLoading, setSimpleLoading] = useState(false);
  const [simpleError, setSimpleError] = useState(null);
  const [simpleSubmitted, setSimpleSubmitted] = useState(false);
  const [simpleDuplicate, setSimpleDuplicate] = useState(null);
  const setSL = (k, v) => setSimpleLead((l) => ({ ...l, [k]: v }));

  useEffect(() => {
    const id = "skdc-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    sendCapi("PageView");
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  const clearResult = () => {
    if (result) {
      setResult(null);
      setLeadSubmitted(false);
      setLeadDuplicate(null);
      setLeadError(null);
    }
  };
  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    clearResult();
  };
  const setL = (k, v) => setLead((l) => ({ ...l, [k]: v }));

  const loanNum = parseFloat(form.loanAmount);
  const amountTooHigh = loanNum > UKK_MAX;
  const amountTooLow = loanNum > 0 && loanNum < UKK_MIN;

  const harmEnd = (() => {
    if (!form.startDate || !form.loanTermMonths) return null;
    const d = new Date(form.startDate);
    d.setMonth(d.getMonth() + parseInt(form.loanTermMonths));
    return d;
  })();
  const harmDeadline = harmEnd
    ? new Date(harmEnd.getFullYear() + 1, harmEnd.getMonth(), harmEnd.getDate())
    : null;
  const harmExpired = harmDeadline && harmDeadline < new Date();
  const repaidDL = repaidDate
    ? new Date(
        new Date(repaidDate).getFullYear() + 1,
        new Date(repaidDate).getMonth(),
        new Date(repaidDate).getDate()
      )
    : null;
  const repaidExpired = repaidDL && repaidDL < new Date();
  const deadlineBlocked = (harmExpired && !isRepaid) || repaidExpired;
  const hasSavings = result && result.lacznieKorzyscZSkd > 0;

  const validateCalc = () => {
    const la = parseFloat(form.loanAmount),
      lt = parseInt(form.loanTermMonths),
      mg = parseFloat(form.margin);
    if (!form.startDate || !la || !lt || isNaN(mg))
      return "Wypełnij wszystkie dane kredytu.";
    if (!bankName.trim()) return "Wybierz nazwę banku.";
    if (la < UKK_MIN)
      return `Minimalna kwota to ${UKK_MIN.toLocaleString("pl-PL")} zł.`;
    if (la > UKK_MAX)
      return `SKD dotyczy kredytów konsumenckich do ${UKK_MAX.toLocaleString(
        "pl-PL"
      )} zł.`;
    if (harmExpired && !isRepaid)
      return `Termin SKD wygasł (${harmEnd?.toLocaleDateString(
        "pl-PL"
      )}). Jeśli spłaciłeś wcześniej — zaznacz poniżej.`;
    if (repaidExpired)
      return `Termin SKD wygasł rok od daty spłaty (${new Date(
        repaidDate
      ).toLocaleDateString("pl-PL")}).`;
    return null;
  };

  const calculate = async () => {
    setCalcError(null);
    setResult(null);
    setLeadSubmitted(false);
    setLeadDuplicate(null);
    setLeadError(null);
    const err = validateCalc();
    if (err) return setCalcError(err);
    const leadErr = validateLead();
    if (leadErr) return setCalcError(leadErr);
    if (isRepaid && !repaidDate)
      return setCalcError("Podaj datę całkowitej spłaty kredytu.");
    setCalcLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
        body: JSON.stringify({
          loanAmount: parseFloat(form.loanAmount),
          loanTermMonths: parseInt(form.loanTermMonths),
          margin: parseFloat(form.margin),
          wiborTenor: form.wiborTenor,
          startDate: form.startDate,
          commissionPLN: parseFloat(form.commissionPLN || "0"),
          bankName: bankName || null,
          ...(isRepaid && repaidDate ? { repaidDate } : {}),
        }),
      });
      if (!res.ok) throw new Error(`Błąd serwera: ${res.status}`);
      const data = await res.json();
      setResult(data);
      await submitLeadAuto(data);
    } catch (e) {
      setCalcError(e.message || "Błąd połączenia — spróbuj ponownie.");
    } finally {
      setCalcLoading(false);
    }
  };

  const validateSimple = () => {
    const { firstName, lastName } = splitFullName(simpleLead.fullName);
    if (!firstName || !lastName) return "Podaj imię i nazwisko.";
    if (!simpleLead.email.includes("@")) return "Podaj poprawny adres e-mail.";
    if (!isValidPhone(simpleLead.phone)) {
      setSimplePhoneError(true);
      return "Podaj poprawny numer telefonu (9 cyfr, opcjonalnie +48).";
    }
    if (simpleLead.creditDetails.trim().length < 10)
      return "Opisz dane kredytu, który mamy przeanalizować.";
    if (!simpleLead.rodoConsent)
      return "Wymagana jest akceptacja Polityki Prywatności.";
    return null;
  };

  const resetSimple = () => {
    setSimpleSubmitted(false);
    setSimpleDuplicate(null);
    setSimpleError(null);
    setSimpleLead({
      fullName: "",
      email: "",
      phone: "",
      creditDetails: "",
      rodoConsent: false,
    });
  };

  const submitSimple = async () => {
    setSimpleError(null);
    setSimpleDuplicate(null);
    const err = validateSimple();
    if (err) return setSimpleError(err);
    setSimpleLoading(true);
    try {
      const { firstName, lastName } = splitFullName(simpleLead.fullName);
      const res = await fetch(`${LEADS_URL}/simple`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
        body: JSON.stringify({
          firstName,
          lastName,
          email: simpleLead.email.trim(),
          phone: simpleLead.phone.trim(),
          creditDetails: simpleLead.creditDetails.trim(),
          source: "kalkulator_publiczny_prosty",
        }),
      });
      if (res.status === 409) {
        const j = await res.json().catch(() => ({}));
        setSimpleDuplicate({
          contactEmail: j.contactEmail || "contact@zwrotyskd.pl",
          existingDate: j.existingDate,
        });
      } else if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Błąd serwera: ${res.status}`);
      } else {
        setSimpleSubmitted(true);
        sendCapi("Lead", { content_name: "prosty-formularz" });
        if (typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18145371254/UVk3CPrX9qgcEPbIscxD",
            value: 1.0,
            currency: "PLN",
          });
        }
      }
    } catch (e) {
      setSimpleError(e.message || "Błąd połączenia — spróbuj ponownie.");
    } finally {
      setSimpleLoading(false);
    }
  };

  const validateLead = () => {
    const { firstName, lastName } = splitFullName(lead.fullName);
    if (!firstName || !lastName) return "Podaj imię i nazwisko.";
    if (!lead.email.includes("@")) return "Podaj poprawny adres e-mail.";
    if (!isValidPhone(lead.phone)) {
      setPhoneError(true);
      return "Podaj poprawny numer telefonu (9 cyfr, opcjonalnie +48).";
    }
    if (!lead.rodoConsent)
      return "Wymagana jest akceptacja Polityki Prywatności.";
    return null;
  };

  const submitLeadAuto = async (resultData) => {
    setLeadError(null);
    setLeadLoading(true);
    try {
      const { firstName, lastName } = splitFullName(lead.fullName);
      const fd = new FormData();
      fd.append("firstName", firstName);
      fd.append("lastName", lastName);
      fd.append("email", lead.email.trim());
      fd.append("phone", lead.phone.trim());
      fd.append("calculationUuid", resultData?.uuid || "");
      fd.append("savingsAmount", resultData?.lacznieKorzyscZSkd ?? 0);
      fd.append("bankName", bankName || "");
      fd.append("loanAmount", form.loanAmount || "");
      fd.append("excelUrl", resultData?.excelUrl || "");
      const res = await fetch(LEADS_URL, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
        body: fd,
      });
      if (res.status === 409) {
        const j = await res.json().catch(() => ({}));
        setLeadDuplicate({
          contactEmail: j.contactEmail || "contact@zwrotyskd.pl",
          existingDate: j.existingDate,
        });
      } else if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Błąd serwera: ${res.status}`);
      } else {
        setLeadSubmitted(true);
        sendCapi("CompleteRegistration", {
          value: resultData?.lacznieKorzyscZSkd ?? 0,
          currency: "PLN",
          bank_name: bankName || null,
        });
        if (typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18145371254/UVk3CPrX9qgcEPbIscxD",
            value: 1.0,
            currency: "PLN",
          });
        }
      }
    } catch (e) {
      setLeadError(e.message || "Błąd połączenia — spróbuj ponownie.");
    } finally {
      setLeadLoading(false);
    }
  };

  const rightPanel = !result ? (
    <KancelariePlaceholder />
  ) : (
    <div className="skdc-au">
      {result.commissionPLN > 0 &&
        (() => {
          const r = result.margin + result.wiborAtStart;
          const nominalEffective = Math.pow(1 + r / 12, 12) - 1;
          return (
            <div
              style={{
                background: "#fff",
                border: "1.5px solid #e4eeec",
                borderRadius: 13,
                padding: "14px 16px",
                marginBottom: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div className="skdc-mc-l">RRSO bez prowizji</div>
                  <div
                    style={{ fontSize: 20, fontWeight: 700, color: "#0f766e" }}
                  >
                    {pct(nominalEffective)}
                  </div>
                </div>
                <div
                  style={{
                    padding: "0 8px 14px",
                    color: "#c8deda",
                    fontSize: 18,
                    fontWeight: 300,
                  }}
                >
                  vs
                </div>
                <div style={{ flex: 1, textAlign: "right" }}>
                  <div className="skdc-mc-l" style={{ textAlign: "right" }}>
                    RRSO rzeczywiste
                  </div>
                  <div
                    style={{ fontSize: 20, fontWeight: 700, color: "#dc2626" }}
                  >
                    {pct(result.rrso)}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      <div className="mg mg-wide" style={{ marginBottom: 8 }}>
        {[
          { l: "Rata wg umowy", v: pln(result.staraRataWgBanku) },
          { l: "Rata po SKD", v: pln(result.nowaRataPoSkd) },
          { l: "Nadwyżka do zwrotu", v: pln(result.nadwyzkaDoZwrotuKlientowi) },
          {
            l: "Oszczędność na ratach",
            v: pln(result.ileMniejSplaciWPrzyszlosci),
          },
        ].map((m) => (
          <div key={m.l} className="skdc-mc">
            <div className="skdc-mc-l">{m.l}</div>
            <div className="skdc-mc-v">{m.v}</div>
          </div>
        ))}
      </div>
      <DeadlineBanner skdStatus={result.skdStatus} />
      {leadSubmitted && (
        <div className="skdc-dl-ok" style={{ marginTop: 8 }}>
          ✓ Zgłoszenie wysłane — prawnik skontaktuje się z Tobą w ciągu{" "}
          <strong>24h roboczych</strong>.
        </div>
      )}
      {leadDuplicate && (
        <div className="skdc-dl-warn" style={{ marginTop: 8 }}>
          ℹ Mamy już Twoje zgłoszenie.{" "}
          <a
            href={`mailto:${leadDuplicate.contactEmail}`}
            style={{ color: "#0f766e", fontWeight: 600 }}
          >
            {leadDuplicate.contactEmail}
          </a>
        </div>
      )}
      {leadError && (
        <div className="skdc-ebox" style={{ marginTop: 8 }}>
          {leadError}
        </div>
      )}
    </div>
  );

  return (
    <div className="skdc-wrap">
      {!result && !simpleSubmitted && (
        <div className="skdc-modes">
          <button
            type="button"
            className={`skdc-mode${mode === "calculator" ? " active" : ""}`}
            onClick={() => setMode("calculator")}
          >
            <span className="skdc-mode-t">Policz samodzielnie</span>
            <span className="skdc-mode-s">Kalkulator korzyści SKD online</span>
          </button>
          <button
            type="button"
            className={`skdc-mode${mode === "simple" ? " active" : ""}`}
            onClick={() => setMode("simple")}
          >
            <span className="skdc-mode-t">
              Zostaw dane — analizę zrobimy my
            </span>
            <span className="skdc-mode-s">
              Wpisujesz dane kredytu, oddzwania prawnik
            </span>
          </button>
        </div>
      )}
      {mode === "simple" ? (
        simpleSubmitted ? (
          <div className="skdc-simple-done">
            <div className="skdc-simple-done-ic">✓</div>
            <h3>Dziękujemy — zgłoszenie przyjęte!</h3>
            <p>
              Otrzymaliśmy Twoje dane. Przygotujemy bezpłatną analizę Twojego
              kredytu pod kątem Sankcji Kredytu Darmowego.
            </p>
            <div className="skdc-simple-steps">
              <div className="skdc-simple-step">
                <span className="skdc-simple-step-n">1</span>
                <span>
                  <b>Wygenerujemy analizę</b> na podstawie podanych danych.
                </span>
              </div>
              <div className="skdc-simple-step">
                <span className="skdc-simple-step-n">2</span>
                <span>
                  <b>Wynik wyślemy na e-mail</b>{" "}
                  {simpleLead.email ? <b>{simpleLead.email}</b> : null}.
                </span>
              </div>
              <div className="skdc-simple-step">
                <span className="skdc-simple-step-n">3</span>
                <span>
                  <b>Przekażemy sprawę prawnikowi</b>, który skontaktuje się z
                  Tobą, aby omówić wynik i dalsze kroki.
                </span>
              </div>
            </div>
            <button
              className="skdc-btn skdc-btn-p"
              onClick={resetSimple}
              style={{ maxWidth: 280, margin: "0 auto" }}
            >
              Wyślij kolejne zgłoszenie
            </button>
          </div>
        ) : (
          <div className="skdc-grid">
            <div className="skdc-card">
              <button
                type="button"
                className="skdc-self-cta"
                onClick={() => setMode("calculator")}
              >
                <span className="skdc-self-cta-spark">⚡</span>
                <span className="skdc-self-cta-txt">
                  <span className="skdc-self-cta-t">
                    Chcesz poznać wynik od razu?
                  </span>
                  <span className="skdc-self-cta-s">
                    Policz korzyść z SKD samodzielnie — zajmie chwilę
                  </span>
                </span>
                <span className="skdc-self-cta-btn">
                  Policz samodzielnie {IC.arrowRight}
                </span>
              </button>
              <div className="skdc-section-head">
                <div className="skdc-section-icon teal">{IC.user}</div>
                <div>
                  <div className="skdc-section-title">
                    Zostaw dane do analizy
                  </div>
                  <div className="skdc-section-sub">
                    Analizę przygotujemy za Ciebie — bezpłatnie
                  </div>
                </div>
              </div>
              <div className="skdc-fg">
                <Field icon={IC.user} label="Imię i nazwisko" span>
                  <input
                    className="skdc-inp"
                    type="text"
                    placeholder="Jan Kowalski"
                    value={simpleLead.fullName}
                    onChange={(e) => setSL("fullName", e.target.value)}
                  />
                </Field>
                <Field icon={IC.mail} label="E-mail" span>
                  <input
                    className="skdc-inp"
                    type="email"
                    placeholder="jan@example.com"
                    value={simpleLead.email}
                    onChange={(e) => setSL("email", e.target.value)}
                  />
                </Field>
                <Field
                  icon={IC.phone}
                  label="Telefon"
                  span
                  warnHint={
                    simplePhoneError
                      ? "Podaj 9-cyfrowy numer, np. 500 100 200"
                      : undefined
                  }
                >
                  <input
                    className={`skdc-inp${simplePhoneError ? " err" : ""}`}
                    type="tel"
                    placeholder="+48 500 100 200"
                    value={simpleLead.phone}
                    onChange={(e) => {
                      setSL("phone", e.target.value);
                      if (simplePhoneError && isValidPhone(e.target.value))
                        setSimplePhoneError(false);
                    }}
                    onBlur={() => {
                      if (simpleLead.phone.trim())
                        setSimplePhoneError(!isValidPhone(simpleLead.phone));
                    }}
                  />
                </Field>
                <Field
                  label="Dane kredytu do analizy"
                  span
                  hint="Podaj wszystkie dane o kredycie, jakie znasz — np. bank, kwota, data umowy, liczba rat, oprocentowanie/marża, prowizja."
                >
                  <textarea
                    className="skdc-inp skdc-ta skdc-bare"
                    placeholder="Podaj wszystkie dane o kredycie, jakie znasz, np.:&#10;Bank: …&#10;Kwota kredytu: …&#10;Data umowy: …&#10;Liczba rat: …&#10;Oprocentowanie / marża: …&#10;Prowizja: …&#10;Dodatkowe informacje: …"
                    value={simpleLead.creditDetails}
                    onChange={(e) => setSL("creditDetails", e.target.value)}
                  />
                </Field>
              </div>
              {simpleError && <div className="skdc-ebox">{simpleError}</div>}
              {simpleDuplicate && (
                <div className="skdc-dl-warn" style={{ marginTop: 10 }}>
                  ℹ Mamy już Twoje zgłoszenie. W sprawie kolejnej analizy napisz
                  na{" "}
                  <a
                    href={`mailto:${simpleDuplicate.contactEmail}`}
                    style={{ color: "#0f766e", fontWeight: 600 }}
                  >
                    {simpleDuplicate.contactEmail}
                  </a>
                </div>
              )}
              <div style={{ marginTop: 13 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    cursor: "pointer",
                    fontSize: 11,
                    color: "#334155",
                    lineHeight: 1.6,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={simpleLead.rodoConsent}
                    onChange={(e) => setSL("rodoConsent", e.target.checked)}
                    style={{
                      width: 16,
                      height: 16,
                      accentColor: "#0f766e",
                      cursor: "pointer",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                  <span>
                    Akceptuję{" "}
                    <a
                      href="https://zwrotyskd.pl/polityka-prywatnosci/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#0f766e", fontWeight: 600 }}
                    >
                      Politykę Prywatności
                    </a>{" "}
                    i wyrażam zgodę na przetwarzanie moich danych osobowych w
                    celu analizy SKD.{" "}
                    <span style={{ color: "#dc2626", fontWeight: 700 }}>*</span>
                  </span>
                </label>
              </div>
              <button
                className="skdc-btn skdc-btn-p"
                onClick={submitSimple}
                disabled={simpleLoading}
                style={{ marginTop: 18 }}
              >
                {simpleLoading ? "Wysyłam…" : "Wyślij zgłoszenie →"}
              </button>
            </div>
            <div className="skdc-right-col">
              <KancelariePlaceholder />
            </div>
          </div>
        )
      ) : result ? (
        <div>
          <div className="skdc-summary-card" style={{ marginBottom: 16 }}>
            <div className="skdc-summary-row">
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#a8c8c2",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    marginBottom: 3,
                  }}
                >
                  Dane kredytu
                </div>
                <div className="skdc-summary-bank">{bankName || "—"}</div>
              </div>
              <button
                className="skdc-btn-reset"
                onClick={() => {
                  setResult(null);
                  setLeadSubmitted(false);
                  setLeadDuplicate(null);
                  setLeadError(null);
                  setCalcError(null);
                }}
              >
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2 8a6 6 0 1 0 1.5-3.9M2 4v4h4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Wyczyść i oblicz ponownie
              </button>
            </div>
            <div className="skdc-summary-chips">
              <span className="skdc-chip">
                {pln(parseFloat(form.loanAmount))}
              </span>
              <span className="skdc-chip">{form.loanTermMonths} rat</span>
              <span className="skdc-chip">Marża {form.margin}%</span>
              <span className="skdc-chip">od {fmtD(form.startDate)}</span>
              {parseFloat(form.commissionPLN) > 0 && (
                <span className="skdc-chip">
                  Prowizja {pln(parseFloat(form.commissionPLN))}
                </span>
              )}
              {isRepaid && repaidDate && (
                <span className="skdc-chip">Spłacony {fmtD(repaidDate)}</span>
              )}
            </div>
          </div>
          {rightPanel}
        </div>
      ) : (
        <div className="skdc-grid">
          <div className="skdc-card">
            <div className="skdc-section-head">
              <div className="skdc-section-icon teal">{IC.bank}</div>
              <div>
                <div className="skdc-section-title">Dane kredytu</div>
                <div className="skdc-section-sub">
                  Informacje z umowy kredytowej
                </div>
              </div>
            </div>
            <div className="skdc-fg">
              <Field
                icon={IC.money}
                label="Kwota kredytu (zł)"
                hint={
                  !amountTooHigh && !amountTooLow
                    ? `Maks. ${UKK_MAX.toLocaleString("pl-PL")} zł`
                    : undefined
                }
                warnHint={
                  amountTooHigh
                    ? "Przekracza limit — kredyty hipoteczne są wyłączone"
                    : amountTooLow
                    ? `Min. ${UKK_MIN.toLocaleString("pl-PL")} zł`
                    : undefined
                }
              >
                <input
                  className={`skdc-inp${
                    amountTooHigh || amountTooLow ? " err" : ""
                  }`}
                  type="number"
                  placeholder="120 000"
                  step="1000"
                  min={UKK_MIN}
                  max={UKK_MAX}
                  value={form.loanAmount}
                  onChange={(e) => setF("loanAmount", e.target.value)}
                />
              </Field>
              <Field
                icon={IC.hash}
                label="Liczba rat"
                warnHint={
                  harmExpired && !isRepaid
                    ? `Termin SKD wygasł ${harmDeadline?.toLocaleDateString(
                        "pl-PL"
                      )}`
                    : undefined
                }
              >
                <input
                  className="skdc-inp"
                  type="number"
                  placeholder="120"
                  step="1"
                  min="1"
                  value={form.loanTermMonths}
                  onChange={(e) => setF("loanTermMonths", e.target.value)}
                />
              </Field>
              <Field icon={IC.percent} label="Marża banku (%)">
                <input
                  className="skdc-inp"
                  type="number"
                  placeholder="7.50"
                  step="0.01"
                  min="0"
                  value={form.margin}
                  onChange={(e) => setF("margin", e.target.value)}
                />
              </Field>
              <Field icon={IC.calendar} label="Data uruchomienia">
                <input
                  className="skdc-inp"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setF("startDate", e.target.value)}
                />
              </Field>
              <Field icon={IC.money} label="Prowizja (zł)" span>
                <input
                  className="skdc-inp"
                  type="number"
                  placeholder="0"
                  step="100"
                  min="0"
                  value={form.commissionPLN}
                  onChange={(e) => setF("commissionPLN", e.target.value)}
                />
              </Field>
            </div>
            <div style={{ marginTop: 14 }}>
              <label className="skdc-lbl">Nazwa banku</label>
              <div className="skdc-iw">
                <span className="skdc-ii">{IC.bank}</span>
                <select
                  className="skdc-inp"
                  value={bankSelect}
                  onChange={(e) => {
                    setBankSelect(e.target.value);
                    setBankCustom("");
                    clearResult();
                  }}
                >
                  <option value="">— wybierz bank —</option>
                  {BANKS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              {bankSelect === "Inny" && (
                <input
                  className="skdc-inp skdc-bare"
                  type="text"
                  placeholder="Wpisz nazwę banku"
                  value={bankCustom}
                  onChange={(e) => {
                    setBankCustom(e.target.value);
                    clearResult();
                  }}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
            <div
              style={{
                background: "#f4faf8",
                borderRadius: 12,
                padding: "13px 15px",
                border: "1.5px solid #dde8e5",
                marginTop: 14,
              }}
            >
              <label
                className="skdc-chk"
                style={{ display: "flex", gap: "5px" }}
              >
                <input
                  type="checkbox"
                  checked={isRepaid}
                  onChange={(e) => {
                    setIsRepaid(e.target.checked);
                    if (!e.target.checked) setRepaidDate("");
                    clearResult();
                  }}
                />
                <span
                  style={{ fontSize: 14, color: "#0a2e2c", fontWeight: 500 }}
                >
                  Kredyt został w całości spłacony
                </span>
              </label>
              {isRepaid && (
                <div style={{ marginTop: 11 }}>
                  <label className="skdc-lbl">Data całkowitej spłaty</label>
                  <div className="skdc-iw">
                    <span className="skdc-ii">{IC.calendar}</span>
                    <input
                      className={`skdc-inp${repaidExpired ? " err" : ""}`}
                      type="date"
                      value={repaidDate}
                      onChange={(e) => {
                        setRepaidDate(e.target.value);
                        clearResult();
                      }}
                    />
                  </div>
                  {repaidExpired ? (
                    <p className="skdc-hwarn">
                      ⚠ Termin SKD wygasł{" "}
                      {repaidDL?.toLocaleDateString("pl-PL")}
                    </p>
                  ) : repaidDate ? (
                    <p className="skdc-hint">
                      Termin złożenia: {repaidDL?.toLocaleDateString("pl-PL")}
                    </p>
                  ) : (
                    <p className="skdc-hint">
                      Termin: 1 rok od tej daty (art. 45 ust. 5 ukk)
                    </p>
                  )}
                </div>
              )}
            </div>
            {calcError && <div className="skdc-ebox">{calcError}</div>}
            <div className="skdc-fg" style={{ marginTop: 14 }}>
              <Field icon={IC.user} label="Imię i nazwisko" span>
                <input
                  className="skdc-inp"
                  type="text"
                  placeholder="Jan Kowalski"
                  value={lead.fullName}
                  onChange={(e) => setL("fullName", e.target.value)}
                />
              </Field>
              <Field icon={IC.mail} label="E-mail" span>
                <input
                  className="skdc-inp"
                  type="email"
                  placeholder="jan@example.com"
                  value={lead.email}
                  onChange={(e) => setL("email", e.target.value)}
                />
              </Field>
              <Field
                icon={IC.phone}
                label="Telefon"
                span
                warnHint={
                  phoneError
                    ? "Podaj 9-cyfrowy numer, np. 500 100 200"
                    : undefined
                }
              >
                <input
                  className={`skdc-inp${phoneError ? " err" : ""}`}
                  type="tel"
                  placeholder="+48 500 100 200"
                  value={lead.phone}
                  onChange={(e) => {
                    setL("phone", e.target.value);
                    if (phoneError && isValidPhone(e.target.value))
                      setPhoneError(false);
                  }}
                  onBlur={() => {
                    if (lead.phone.trim())
                      setPhoneError(!isValidPhone(lead.phone));
                  }}
                />
              </Field>
            </div>
            <div style={{ marginTop: 13 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#334155",
                  lineHeight: 1.6,
                }}
              >
                <input
                  type="checkbox"
                  checked={lead.rodoConsent}
                  onChange={(e) => setL("rodoConsent", e.target.checked)}
                  style={{
                    width: 16,
                    height: 16,
                    accentColor: "#0f766e",
                    cursor: "pointer",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />
                <span>
                  Akceptuję{" "}
                  <a
                    href="https://zwrotyskd.pl/polityka-prywatnosci/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#0f766e", fontWeight: 600 }}
                  >
                    Politykę Prywatności
                  </a>{" "}
                  i wyrażam zgodę na przetwarzanie moich danych osobowych w celu
                  analizy SKD.{" "}
                  <span style={{ color: "#dc2626", fontWeight: 700 }}>*</span>
                </span>
              </label>
            </div>
            <button
              className="skdc-btn skdc-btn-p"
              onClick={calculate}
              disabled={calcLoading || amountTooHigh || deadlineBlocked}
              style={{ marginTop: 18 }}
            >
              {calcLoading ? "Obliczam…" : "Oblicz korzyści SKD →"}
            </button>
          </div>
          <div className="skdc-right-col">{rightPanel}</div>
        </div>
      )}
    </div>
  );
}
